import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import StyledRadioGroup from 'src/components/StyledRadioGroup';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { type TeamMemberRole } from 'src/store/sageHomeApi';
import { selectTeamRole, setTeamRole } from 'src/store/slices/onboardingSlice';
import { useAppDispatch } from 'src/store/store';

export const Route = createFileRoute('/(app)/onboarding/select-role')({
    component: SelectRoleComponent,
});

const roleOptions: Array<{ value: TeamMemberRole; label: string }> = [
    {
        value: TeamRoles.CommunityCaregiver,
        label: "I'm setting up Sage to support a loved one with their care.",
    },
    {
        value: TeamRoles.CareRecipient,
        label: "I'm setting up Sage to manage my own care.",
    },
];

function SelectRoleComponent() {
    const dispatch = useAppDispatch();

    const teamRole = useSelector(selectTeamRole);

    const [role, setRole] = useState<TeamMemberRole | undefined>(teamRole);
    const { progress, nextStep, previousStep, updateAvailableSteps, canGoBack } =
        useOnboardingWorkflow();

    const handleNext = async () => {
        if (role) {
            dispatch(setTeamRole(role));
        }

        nextStep();
    };

    const handleRoleChange = (value: string) => {
        updateAvailableSteps(value as TeamMemberRole);
        setRole(value as TeamMemberRole);
    };

    return (
        <>
            <PageHeader
                hideBackButton={!canGoBack}
                onBack={canGoBack ? previousStep : undefined}
                hideActionButton
            >
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>
            <Stack sx={{ gap: 4 }}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                        Who is receiving care?
                    </Typography>
                    <Typography variant="body1">
                        Please select the role that describes your relationship with the person
                        whose care will be managed in Sage.
                    </Typography>
                </Stack>
                <StyledRadioGroup
                    options={roleOptions}
                    value={role || ''}
                    onChange={handleRoleChange}
                    labelPlacement="start"
                />
            </Stack>
            <PageFooter>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={!role}
                    onClick={handleNext}
                    sx={{ mt: 2 }}
                >
                    Continue
                </Button>
            </PageFooter>
        </>
    );
}
